import type { EpochRecord, ExtinctionLawName, SpectralState } from "../data/types";
import { epochLabel, stateColor } from "../data/types";
import type { Mode } from "../domain/mode";

interface Props {
  mode: Mode;
  epochCount: number;
  /** Date of the soloed epoch in all-epochs mode, if any. */
  soloedDate: string | null;
  primary: EpochRecord;
  comparison: EpochRecord | null;
  reddened: { state: SpectralState; av: number; law: ExtinctionLawName } | null;
  maximised: boolean;
  onToggleMaximised: () => void;
}

const MODE_PILLS: Array<{ mode: Mode; label: string }> = [
  { mode: "observed", label: "Observed" },
  { mode: "interpolated", label: "Interpolated" },
  { mode: "reddening", label: "Dust" },
  { mode: "all_epochs", label: "All" },
];

export function PlotToolbar(props: Props) {
  const { mode, epochCount, soloedDate, primary, comparison, reddened, maximised, onToggleMaximised } = props;
  return (
    <div className="plot-toolbar">
      <div className="group">
        <label>Mode</label>
        {MODE_PILLS.map((p) => (
          <span key={p.mode} className={`pill ${mode === p.mode ? "active" : ""}`}>
            {p.label}
          </span>
        ))}
      </div>
      {mode === "all_epochs" ? (
        <div className="group">
          <label>Stacked</label>
          <span>
            {epochCount} epochs
            {soloedDate ? ` · soloing ${soloedDate}` : ""}
          </span>
        </div>
      ) : (
        <div className="group">
          <label>Primary</label>
          <span style={{ color: stateColor(primary.state) }}>{epochLabel(primary)}</span>
        </div>
      )}
      {comparison ? (
        <div className="group">
          <label>Compare</label>
          <span style={{ color: stateColor(comparison.state) }}>{epochLabel(comparison)}</span>
        </div>
      ) : null}
      {reddened ? (
        <div className="group">
          <label>Reddened</label>
          <span style={{ color: "var(--reddened-color)" }}>
            {reddened.state} × A_V {reddened.av.toFixed(2)} ({reddened.law})
          </span>
        </div>
      ) : null}
      <button
        className={`plot-maximise${maximised ? " active" : ""}`}
        onClick={onToggleMaximised}
        aria-pressed={maximised}
        title={maximised ? "Restore side panels" : "Maximise spectrum and timeline"}
      >
        {maximised ? "⤡ Restore" : "⤢ Maximise"}
      </button>
    </div>
  );
}
